/**
 * Shona catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Standard Shona (chiShona) at a mathematician's register. **Warrant → «Humbowo»**
 * (proof/warrant — flagged). Scientific loanwords are used where Shona standardly borrows (logic
 * → Lojiki, modal, heuristic, scalar, geometry). W/M grades stay Latin (`::numbering-system/latn`).
 * CLDR cardinal categories are one / other; the noun uses the mu-/mi- class (muganhu → miganhu).
 * `frameActive` declares one/other plus an explicit `=0`. Several abstract terms are flagged
 * "expert-review suggested" in `Agent9_Batch3_REPORT.md`. Per INV-STABLE-ID a wrong translation
 * is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const SN_CATALOG = {
    locale: 'sn',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Ongororo',
        [P2.sectionClaims]: 'Zvirevo',
        [P2.sectionIntegrity]: 'Kuvimbika',
        [P2.axisWarrantLabel]: 'Humbowo',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Zvemuyedzo',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Tsanangudzo} interp {Dudziro} testable {Zvinogona kuyedzwa} tested {Zvakayedzwa} refuted {Zvakarambwa} program {Chirongwa chetsvakurudzo} philos {Zveuzivi} other {Hazvizivikanwi}}',
        [P2.axisModalLabel]: 'Modaro',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Mwero wesimba {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Zvinogona kuferefetwa',
        [P2.stateRefuted]: 'Zvakarambwa',
        [P2.stateOutside]: 'Kunze kwenharaunda',
        [P2.verdictConflict]: 'Zvinopesana ne-{constraints}',
        [P2.verdictOutside]: 'Zvinoda {missing}',
        [P2.lossRegisterPlain]: 'Zvakaitwa nyore mumutauro wakajairika — humwe ukwirira hwakasiyiwa.',
        [P2.lossRegisterKid]: 'Zvakaitwa nyore kuvaverengi vadiki — mashoko mazhinji akasiyiwa.',
        [P2.lossProfileDetail]: 'Purofairo yakazara ye-Humbowo / Zvemuyedzo / Modaro yakapfupikiswa pano.',
        [P2.lossConflictDetail]: 'Tsananguro dzekupokana dzakapfupikiswa pakuona uku.',
        [P2.laneVerified]: 'Zvakasimbiswa',
        [P2.laneUnverified]: 'Hazvina kusimbiswa',
        [P2.laneConflict]: 'Kupokana kwakaratidzwa',
        [P2.constraintLogic]: 'Lojiki',
        [P2.constraintLightBends]: 'Chiedza chinokombama mumunda wesimba rekudhonza',
        [P2.constraintParallelPostulate]: 'Postureti yemitsetse yakaenzana',
        [P2.claimScalarGravity]: 'Dzidziso ye-scalar yesimba rekudhonza',
        [P2.claimNonEuclidean]: 'Jiyomendi isiri yaEuclid',
        [P2.claimSubstrateSr]: 'Dudziro ye-substrate yehukama hwakakosha',
        [P2.edgeExtObservation]: 'Kucherechedza kwekunze',
        [P2.edgeResourceSample]: 'Zviwanikwa zvekuverenga zvinogoverana',
        [P2.edgeHeuristicSample]: 'Nhungamiro ye-heuristic',
        [P2.edgeSharedSample]: 'Hwaro hunogoverwa',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Miganhu',
        [P2.sectionEdges]: 'Migumo',
        [P2.sectionFrame]: 'Chimiro',
        [P2.sectionExposure]: 'Kuratidzwa',
        [P2.statusAdoptedFormal]: 'Zvakagamuchirwa (zviri pamutemo)',
        [P2.statusAdoptedInterpretive]: 'Zvakagamuchirwa (zvedudziro)',
        [P2.statusSuspended]: 'Zvakamiswa',
        [P2.edgeEffectConstraint]: 'Inogadzira muganhu (inogona kuramba)',
        [P2.edgeEffectResource]: 'Zviwanikwa — hazvina simba pamitongo',
        [P2.edgeEffectHeuristic]: 'Heuristic — hazvina simba pamitongo',
        [P2.edgeEffectShared]: 'Hwaro hunogoverwa — hazvina simba pamitongo',
        [P2.frameActive]: '{count, plural, =0 {Hapana miganhu inoshanda} one {# muganhu unoshanda} other {# miganhu inoshanda}}',
        [P2.exposureNone]: 'Hapana kutsamira kwedudziro',
        [P2.exposureDep]: 'Zvinotsamira pa-{id}',
        [P2.laneTrackMachine]: 'Muchina',
        [P2.laneTrackExpert]: 'Nyanzvi',
        [P2.laneTrackCommunity]: 'Nharaunda',
        [P2.expertAudited]: 'Zvakaongororwa',
        [P2.expertInProgress]: 'Zviri kuitwa',
        [P2.expertNotReviewed]: 'Hazvina kuongororwa',
        [P2.communityFlags]: 'Pane mariki',
        [P2.communityNone]: 'Hapana mariki',
        [P2.controlChallenge]: 'Ramba',
        [P2.controlActivate]: 'Mutsa',
        [P2.controlMakeInterpretive]: 'Ita yedudziro',
        [P2.controlImportEdge]: 'Pinza mugumo',
        [P2.controlPromote]: 'Kwidziridza',
        [P2.controlUndo]: 'Dzosera',
        [P2.controlRedo]: 'Itazve',
        [P2.controlRegister]: 'Nyoresa',
        [P2.promoteRejected]: 'Kukwidziridzwa kwarambwa',
        [P2.promoteAccepted]: 'Kukwidziridzwa kwagamuchirwa',
        [P2.promoteReasonRefutationTerminal]: 'Humbowo hahugoni kurapa kurambwa.',
        [P2.promoteReasonKeystone]: 'Kukwidziridzwa kwakakosha kunoda rutsigiro rwemuyedzo.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Rudzi rwemuyedzo haruna kugadziriswa.',
        [P2.promoteReasonModalNotFixed]: 'Mwero we-modal hauna kugadziriswa.',
        [P2.promoteReasonWarrantNotImproved]: 'Mwero we-humbowo hauna kuvandudzwa.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=sn.js.map